import React, { useState, useEffect, useRef } from 'react';
import { Plus, FileSearch, Wrench } from 'lucide-react';
import { useProjectStore } from '../../stores/projectStore';
import { uploadWorkspaceFile } from '../../apiService';
import CorpusShowcase from '../landing/CorpusShowcase';
import BuildShowcase, { type TopicGroup } from '../landing/BuildShowcase';
import ProjectSeedComposer from './ProjectSeedComposer';

interface NewProjectFormProps {
    userId: string;
    // `initialMessage` is the raw text the user typed on the composer, if any.
    // Parent is expected to seed the workspace chat with it and auto-send.
    // Blank-project path calls this with no message.
    onCreated: (projectId: string, initialMessage?: string) => void;
    onProjectSelect?: (projectId: string) => void;
    projectType?: 'research' | 'build';
    /** Build-only: called when the user presses a topic box. Parent
     *  sets transient draft state and navigates to the plan workspace
     *  in draft mode. No project is created here — the project is
     *  created lazily on the user's first chat message (see App.tsx). */
    onBoxDraft?: (topic: TopicGroup) => void;
}

export function deriveProjectName(msg: string): string {
    // Sanitise before truncation. This becomes the persisted project name
    // and is rendered across the left nav, the recent-projects list, and
    // any audit / reporting surface the backend produces. Client-side
    // sanitisation is defence-in-depth — the backend is expected to
    // escape on output — but for a RegTech product names can end up in
    // PDF exports and admin dashboards where escaping is uneven. So we
    // conservatively strip:
    //   - Angle brackets and HTML-entity ampersands (XSS on unsafe paths)
    //   - Quote characters (attribute / SQL / template injection vectors)
    //   - Unicode directional overrides (bidi spoofing in audit trails)
    //   - Zero-width characters (visual deception in text output)
    //   - ASCII and C1 control chars (display garbage, log-injection)
    //   - Collapse runs of whitespace so pasted newlines don't leak
    const sanitised = msg
        .replace(/[<>"'`&]/g, '')
        .replace(/[\u202A-\u202E\u2066-\u2069]/g, '')
        .replace(/[\u200B-\u200D\uFEFF]/g, '')
        // eslint-disable-next-line no-control-regex
        .replace(/[\x00-\x1F\x7F-\x9F]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    // Strip trailing punctuation along with any whitespace that ended up
    // adjacent to it after the control-char-to-space conversion above.
    const clean = sanitised.replace(/[\s?!.]+$/g, '');
    if (!clean) return 'Untitled';
    if (clean.length <= 60) return clean;
    const cut = clean.slice(0, 60);
    const lastSpace = cut.lastIndexOf(' ');
    return (lastSpace > 30 ? cut.slice(0, lastSpace) : cut) + '…';
}

// BAR-523: derive the first-turn seed message + project name from what the user
// typed and/or attached. A file-only start (empty text) MUST still yield a
// non-empty seed, otherwise the auto-send never fires and the uploads are
// orphaned. Pure + exported for unit testing.
export function deriveSeedAndName(
    message: string,
    files: { name: string }[],
    projectType: 'research' | 'build',
): { seed: string; name: string } {
    const trimmed = message.trim();
    const seed = trimmed || (files.length > 0
        ? `I've attached ${files.length} file${files.length > 1 ? 's' : ''} to get started — please review ${files.length > 1 ? 'them' : 'it'}.`
        : '');
    // deriveProjectName('') returns the placeholder 'Untitled', so seed the name
    // from the message, else the first filename, else a type-specific fallback.
    const nameSeed = trimmed || (files.length > 0 ? files[0].name : '');
    const name = nameSeed
        ? deriveProjectName(nameSeed)
        : (projectType === 'build' ? 'Untitled build' : 'Untitled research');
    return { seed, name };
}

function normalizeType(raw: string | undefined): 'research' | 'build' | 'lookup' {
    if (raw === 'build') return 'build';
    if (raw === 'lookup' || raw === 'quick_lookup') return 'lookup';
    return 'research';
}

// ---------------------------------------------------------------------------
// Row rendering helpers. These mirror ProjectLibraryView so the recent-projects
// list on this page reads as the same row component visually. When a third
// consumer appears, extract these (and the row JSX below) into a shared
// src/components/projects/ProjectRow.tsx module.
// ---------------------------------------------------------------------------

type DerivedStatus = 'active' | 'stuck' | 'shipped' | 'archived';

const STUCK_THRESHOLD_MS = 14 * 24 * 60 * 60 * 1000;

function deriveStatus(r: { status: string; has_report: boolean; updated_at: string }): DerivedStatus {
    if (r.status === 'archived') return 'archived';
    if (r.has_report) return 'shipped';
    if (Date.now() - new Date(r.updated_at).getTime() > STUCK_THRESHOLD_MS) return 'stuck';
    return 'active';
}

function relativeTime(iso: string): string {
    const delta = Date.now() - new Date(iso).getTime();
    const s = Math.floor(delta / 1000);
    if (s < 60) return 'just now';
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    const mo = Math.floor(d / 30);
    if (mo < 12) return `${mo}mo ago`;
    return `${Math.floor(mo / 12)}y ago`;
}

const STATUS_STYLE: Record<DerivedStatus, { label: string; dot: string; text: string }> = {
    active:   { label: 'Active',   dot: 'bg-success',         text: 'text-success' },
    stuck:    { label: 'Stuck',    dot: 'bg-warning',         text: 'text-warning' },
    shipped:  { label: 'Shipped',  dot: 'bg-node-definition', text: 'text-node-definition' },
    archived: { label: 'Archived', dot: 'bg-fg-tertiary',     text: 'text-fg-tertiary' },
};

const TYPE_STYLE: Record<'research' | 'build', { label: string; bg: string; text: string; icon: React.ComponentType<any> }> = {
    research: { label: 'RESEARCH', bg: 'bg-[color:var(--bark-teal-light)]',   text: 'text-[color:var(--bark-teal)]', icon: FileSearch },
    build:    { label: 'BUILD',    bg: 'bg-accent-copper-tint',               text: 'text-accent-copper',            icon: Wrench },
};

const MAX_RECENT = 10;

function NewProjectForm({ userId, onCreated, onProjectSelect, projectType = 'research', onBoxDraft }: NewProjectFormProps) {
    const [seedText, setSeedText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { projectList, listLoading, loadProjectList, createProject } = useProjectStore();
    const hasFetched = useRef(false);

    // Ensure project list is loaded so we can show the user's own recent
    // projects on this page — useful if they arrived directly via left-nav
    // rather than going through the home page first.
    useEffect(() => {
        if (userId && !hasFetched.current && !listLoading) {
            hasFetched.current = true;
            loadProjectList(userId);
        }
    }, [userId, listLoading, loadProjectList]);

    const matchingProjects = projectList.filter((p) => normalizeType(p.project_type) === projectType);
    const recentProjects = [...matchingProjects]
        .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
        .slice(0, MAX_RECENT);
    const overflowCount = Math.max(matchingProjects.length - MAX_RECENT, 0);

    const submitProject = async (
        name: string,
        description: string,
        initialMessage?: string,
        attachedFiles?: File[],
    ) => {
        setError(null);

        // BAR-40: never create an owner-less project. `userId` is the logged-in
        // user's email (or uid); if it's empty the user object hasn't hydrated
        // yet — refuse rather than create a project with an empty owner id.
        if (!userId) {
            setError('Still loading your account — please wait a moment and try again.');
            return;
        }

        setIsSubmitting(true);

        // Phase 1: create the project. Scoped try/catch so only the
        // createProject call can produce a user-facing "create failed" error.
        let project;
        try {
            project = await createProject(userId, name, description, projectType);
        } catch (err) {
            console.error('[NewProjectForm] createProject failed:', err);
            const detail = err instanceof Error && err.message
                ? err.message
                : 'Failed to create project. Please try again.';
            setError(detail);
            setIsSubmitting(false);
            return;
        }
        if (!project) {
            setError('Failed to create project. Please try again.');
            setIsSubmitting(false);
            return;
        }

        // BAR-523: files attached at project start. The project (and its home
        // workspace `ws_{project_id}`) now exists, so upload them via the same
        // audited chat door the in-project composer uses, then stash them for
        // GeminiChatArea's auto-sent first turn. All-or-nothing: on any failure
        // we still navigate into the project (it's created) rather than dead-end
        // — successful uploads remain in the workspace and can be re-referenced.
        // Clear any prior project's unconsumed stash so at most one exists.
        useProjectStore.getState().setFirstTurnUploads(null);

        const filesToUpload = attachedFiles || [];
        if (filesToUpload.length > 0) {
            // Hoist to a const so the upload closures capture the id string, not
            // `project` (capturing `project` disables TS's evolving-any on it).
            const projectId = project.project_id;
            const workspaceId = `ws_${projectId}`;
            // Upload in parallel — the workspace already exists and individual
            // uploads have no ordering dependency. Partial success is preserved:
            // keep the ones that landed, log the ones that didn't.
            const settled = await Promise.allSettled(
                filesToUpload.map((file) =>
                    uploadWorkspaceFile(workspaceId, userId, projectId, file, file.name),
                ),
            );
            const uploaded = settled.flatMap((outcome, i) => {
                if (outcome.status === 'fulfilled' && outcome.value.success && outcome.value.uploaded_file) {
                    return [outcome.value.uploaded_file];
                }
                const reason = outcome.status === 'rejected' ? outcome.reason : outcome.value.error;
                console.error('[NewProjectForm] upload failed for', filesToUpload[i].name, reason);
                return [];
            });
            // Attach whatever succeeded to the first turn — partial success is
            // still useful; failed files are simply not attached (they'd 404 the
            // stash otherwise). A visible partial-failure notice is a follow-up.
            if (uploaded.length > 0) {
                useProjectStore.getState().setFirstTurnUploads({ projectId, files: uploaded });
            }
        }

        // Smart project title (BAR-320 follow-up): when the user started from a
        // message, rename from its topic instead of the truncated text. Blank
        // projects have no topic yet, so they keep their "Untitled …" name.
        const titleSeed = (initialMessage || '').trim();
        if (titleSeed) {
            void useProjectStore.getState().autoTitleProject(project.project_id, userId, titleSeed, name);
        }

        // Phase 2: hand off to the parent's navigation callback. The project
        // has already been created server-side at this point, so a throw from
        // onCreated is a parent-side navigation bug — NOT a reason to show a
        // "create failed" banner (the user would retry and end up with a
        // duplicate project). Log it, unlock the form so the user can find
        // the project via the library, but do not surface a create error.
        try {
            onCreated(project.project_id, initialMessage);
            // Success: parent navigates away, unmount handles isSubmitting.
            // (Clearing here would cause a brief flicker right before the
            // view transition, so we deliberately leave it set.)
        } catch (err) {
            console.error(
                '[NewProjectForm] onCreated callback threw after successful project creation:',
                err,
            );
            setIsSubmitting(false);
        }
    };

    const handleComposerSubmit = (message: string, files: File[] = []) => {
        const { seed, name } = deriveSeedAndName(message, files, projectType);
        // seed doubles as the project description and the auto-sent first message.
        submitProject(name, seed, seed, files);
    };

    const handleBlankProject = () => {
        const defaultName = projectType === 'build' ? 'Untitled build' : 'Untitled research';
        submitProject(defaultName, '');
    };

    const typeLabel = projectType === 'build' ? 'Build' : 'Research';

    return (
        <div className="h-full overflow-y-auto bg-bg">
            <div className="w-full max-w-[1220px] mx-auto px-8 pt-16 pb-20">
                {/* Header — copy invites chat, no mention of naming mechanics */}
                <div className="max-w-[880px] mb-6">
                    <h2 className="text-xl font-semibold text-fg mb-1">
                        {projectType === 'build' ? 'New Build Project' : 'New Research Project'}
                    </h2>
                    <p className="text-sm text-fg-tertiary">
                        {projectType === 'build'
                            ? 'Describe what you want to build.'
                            : 'Ask a regulatory question to get started.'}
                    </p>
                </div>

                {/* Primary: composer (visual parity with home-page chat) */}
                <div className="max-w-[880px] mb-3">
                    <ProjectSeedComposer
                        key={projectType}
                        projectType={projectType}
                        seedText={seedText}
                        onSeedConsumed={() => setSeedText('')}
                        onSubmit={handleComposerSubmit}
                        isSubmitting={isSubmitting}
                    />
                    {error && (
                        <div className="mt-3 px-3 py-2 bg-danger-bg border border-danger rounded-md">
                            <p className="text-xs text-danger">{error}</p>
                        </div>
                    )}
                </div>

                {/* Start-blank button, right under the composer. Ghost style so
                    it reads as a proper button (not legal fine print), while
                    staying quieter than the composer's send. */}
                <div className="max-w-[880px] mb-12">
                    <button
                        type="button"
                        onClick={handleBlankProject}
                        disabled={isSubmitting}
                        className="group inline-flex items-center gap-2 px-3 py-2 text-[13px] text-fg-secondary hover:text-accent hover:bg-accent-tint rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Plus className="w-4 h-4 text-fg-tertiary group-hover:text-accent transition-colors" strokeWidth={1.75} />
                        <span>Start a blank {typeLabel} project</span>
                    </button>
                </div>

                {/* Secondary: showcase. Research chips seed the composer above;
                    Build boxes navigate into draft plan mode (no project created
                    here — App.tsx creates lazily on first chat message). */}
                <div className="mb-12">
                    {projectType === 'research'
                        ? <CorpusShowcase onChipClick={setSeedText} />
                        : <BuildShowcase onBoxDraft={onBoxDraft} disabled={isSubmitting} />}
                </div>

                {/* Bottom: user's own recent projects of this type. Row shape
                    mirrors ProjectLibraryView's ProjectRow — icon tile, name
                    + type label, status pill, relative timestamp. Clicking a
                    row opens the project in workspace. */}
                {recentProjects.length > 0 && (
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <span className="font-body text-[11px] font-semibold uppercase tracking-[0.08em] text-fg-secondary">
                                Recent {typeLabel.toLowerCase()} projects
                            </span>
                            {overflowCount > 0 && (
                                <span className="font-mono text-[11px] text-fg-tertiary tabular-nums">
                                    +{overflowCount} more in library
                                </span>
                            )}
                        </div>
                        <div className="border border-border-default rounded-lg bg-surface overflow-hidden">
                            {recentProjects.map((p, idx) => {
                                const pt = normalizeType(p.project_type);
                                // normalizeType can return 'lookup' but those are filtered out
                                // above; fall back to research so the icon table stays exhaustive.
                                const ts = TYPE_STYLE[pt === 'build' ? 'build' : 'research'];
                                const TypeIcon = ts.icon;
                                // `p` is a ProjectSummary (apiService.ts) which declares
                                // status and has_report as required fields, so deriveStatus
                                // can read them directly. No cast needed; if the upstream
                                // shape ever drifts, TypeScript will flag it here rather
                                // than silently rendering every project as "Active".
                                const status = deriveStatus({
                                    status: p.status,
                                    has_report: p.has_report,
                                    updated_at: p.updated_at,
                                });
                                const statusStyle = STATUS_STYLE[status];
                                return (
                                    <div
                                        key={p.project_id}
                                        onClick={() => { if (!isSubmitting && onProjectSelect) onProjectSelect(p.project_id); }}
                                        role="button"
                                        tabIndex={isSubmitting || !onProjectSelect ? -1 : 0}
                                        onKeyDown={(e) => {
                                            if ((e.key === 'Enter' || e.key === ' ') && !isSubmitting && onProjectSelect) {
                                                e.preventDefault();
                                                onProjectSelect(p.project_id);
                                            }
                                        }}
                                        className={`group grid items-center gap-4 px-4 py-3 cursor-pointer transition-colors hover:bg-bg-warm ${idx > 0 ? 'border-t border-divider' : ''} ${(isSubmitting || !onProjectSelect) ? 'cursor-not-allowed opacity-70' : ''}`}
                                        style={{ gridTemplateColumns: '28px minmax(0,1fr) auto auto' }}
                                    >
                                        {/* Type icon tile */}
                                        <div className={`w-7 h-7 rounded-md flex items-center justify-center ${ts.bg}`}>
                                            <TypeIcon className={`w-3.5 h-3.5 ${ts.text}`} strokeWidth={1.75} />
                                        </div>

                                        {/* Name + type label */}
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold text-fg truncate">{p.name}</div>
                                            <div className={`font-body text-[10px] font-bold uppercase tracking-[0.08em] ${ts.text} mt-0.5`}>
                                                {ts.label}
                                            </div>
                                        </div>

                                        {/* Status pill */}
                                        <div className="flex items-center gap-1.5 font-mono text-[11px] font-semibold uppercase tracking-wider min-w-[84px]">
                                            <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`} />
                                            <span className={statusStyle.text}>{statusStyle.label}</span>
                                        </div>

                                        {/* Relative timestamp */}
                                        <div className="font-mono text-[11px] text-fg-tertiary tabular-nums min-w-[72px] text-right">
                                            {relativeTime(p.updated_at)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default NewProjectForm;
